/**
 * 兼容层 - 为V2数据库提供同步接口的兼容性
 * 使用缓存机制支持同步调用
 */

import { vocabularyDBV2 } from '@/lib/vocabularyDB-v2';
import { getAllPossibleLemmas } from '@/lib/vocabularyLevel/word-lemmatizer';

// 本地缓存，提供同步访问
class VocabularyCompatibilityLayer {
  private cache = new Map<string, boolean>();
  private translationCache = new Map<string, string[]>();
  private pronunciationCache = new Map<string, string>();
  private lemmaCache = new Map<string, string>();
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private currentSourceFile: string | null = null; // 当前选择的词库文件

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  private async _doInitialize(): Promise<void> {
    try {
      // 使用 SQLite 数据库
      await vocabularyDBV2.initialize();
      this.initialized = true;
      console.log('🔄 词汇兼容层初始化完成 (SQLite)');
    } catch (error) {
      console.error('❌ SQLite 初始化失败:', error);
      throw error;
    }
  }

  // 预加载常用词汇到缓存
  async preloadCommonWords(words: string[]): Promise<void> {
    if (!this.initialized) await this.initialize();
    
    try {
      if (this.currentSourceFile) {
        // 如果设置了特定词库，逐个查询该词库（包括词形还原）
        for (const word of words) {
          await this.loadWordAsync(word); // 使用已有的词形还原逻辑
        }
      } else {
        // 收集所有可能的词形
        const allPossibleWords = new Set<string>();
        const wordToLemmasMap = new Map<string, string[]>();
        
        words.forEach(word => {
          const possibleLemmas = getAllPossibleLemmas(word.toLowerCase());
          wordToLemmasMap.set(word.toLowerCase(), possibleLemmas);
          possibleLemmas.forEach(lemma => allPossibleWords.add(lemma));
        });
        
        // 批量查询所有可能的词形
        const results = await vocabularyDBV2.getWords(Array.from(allPossibleWords));
        
        // 为每个原始单词分配最佳匹配结果
        words.forEach(word => {
          const wordLower = word.toLowerCase();
          const possibleLemmas = wordToLemmasMap.get(wordLower) || [wordLower];
          
          let foundResult = null;
          let foundLemma = null;
          
          // 查找第一个有结果的词形
          for (const lemma of possibleLemmas) {
            const result = results.get(lemma);
            if (result) {
              foundResult = result;
              foundLemma = lemma;
              break;
            }
          }
          
          // 缓存结果
          this.cache.set(wordLower, foundResult !== null);
          
          if (foundResult) {
            this.translationCache.set(wordLower, [foundResult.translation]);
            if (foundResult.usphone) {
              this.pronunciationCache.set(wordLower, foundResult.usphone);
            }
            if (foundLemma) {
              this.lemmaCache.set(wordLower, foundLemma);
            }
            
            // 同时缓存找到的原形词
            if (foundLemma && foundLemma !== wordLower) {
              this.cache.set(foundLemma, true);
              this.translationCache.set(foundLemma, [foundResult.translation]);
              if (foundResult.usphone) {
                this.pronunciationCache.set(foundLemma, foundResult.usphone);
              }
              this.lemmaCache.set(foundLemma, foundLemma);
            }
            
            if (foundLemma !== wordLower) {
              console.log(`🔍 批量预加载词形还原: "${wordLower}" -> "${foundLemma}"`);
            }
          }
        });
      }
    } catch (error) {
      console.error('预加载失败:', error);
    }
  }

  // 同步检查词汇（使用缓存）
  hasTranslation(word: string): boolean {
    const wordLower = word.toLowerCase();
    
    if (this.cache.has(wordLower)) {
      return this.cache.get(wordLower)!;
    }
    
    // 如果缓存中没有，异步加载并返回false（保守策略）
    // 但是为了提高命中率，我们会在后台异步预加载
    this.loadWordAsync(word);
    return false;
  }

  // 新增：异步检查词汇方法，等待数据库查询完成
  async hasTranslationAsync(word: string): Promise<boolean> {
    const wordLower = word.toLowerCase();
    
    // 先检查缓存
    if (this.cache.has(wordLower)) {
      return this.cache.get(wordLower)!;
    }
    
    // 如果缓存中没有，等待异步加载完成
    await this.loadWordAsync(word);
    
    // 返回加载后的结果
    return this.cache.get(wordLower) || false;
  }

  // 同步获取翻译（使用缓存）
  getTranslation(word: string): string[] {
    const wordLower = word.toLowerCase();
    
    if (this.translationCache.has(wordLower)) {
      return this.translationCache.get(wordLower)!;
    }
    
    // 如果缓存中没有，异步加载并返回空数组
    this.loadWordAsync(word);
    return [];
  }

  // 同步获取发音（使用缓存）
  getUSPronunciation(word: string): string | null {
    const wordLower = word.toLowerCase();
    
    if (this.pronunciationCache.has(wordLower)) {
      return this.pronunciationCache.get(wordLower)!;
    }
    
    // 如果缓存中没有，异步加载并返回null
    this.loadWordAsync(word);
    return null;
  }

  getLemma(word: string): string | null {
    const wordLower = word.toLowerCase();
    if (this.lemmaCache.has(wordLower)) {
      return this.lemmaCache.get(wordLower)!;
    }
    this.loadWordAsync(word);
    return null;
  }

  // 异步加载单词到缓存
  private async loadWordAsync(word: string): Promise<void> {
    try {
      if (!this.initialized) await this.initialize();
      
      const wordLower = word.toLowerCase();
      
      let result = null;
      let matchedLemma: string | null = null;
      
      // 获取所有可能的词形（包括原形）
      const possibleLemmas = getAllPossibleLemmas(wordLower);
      
      // 尝试查找每个可能的词形
      for (const lemma of possibleLemmas) {
        if (this.currentSourceFile) {
          result = await vocabularyDBV2.getWordFromSource(lemma, this.currentSourceFile);
        } else {
          result = await vocabularyDBV2.getWord(lemma);
        }
        
        if (result) {
          matchedLemma = lemma;
          // 找到了，缓存原始查询词的结果
          this.cache.set(wordLower, true);
          this.translationCache.set(wordLower, [result.translation]);
          if (result.usphone) {
            this.pronunciationCache.set(wordLower, result.usphone);
          }
          if (matchedLemma) {
            this.lemmaCache.set(wordLower, matchedLemma);
          }
          
          // 同时缓存找到的原形词
          if (lemma !== wordLower) {
            this.cache.set(lemma, true);
            this.translationCache.set(lemma, [result.translation]);
            if (result.usphone) {
              this.pronunciationCache.set(lemma, result.usphone);
            }
            this.lemmaCache.set(lemma, lemma);
          }
          
          // console.log(`🔍 词形还原成功: "${wordLower}" -> "${lemma}" (找到释义)`);
          return;
        }
      }
      
      // 所有词形都没找到
      this.cache.set(wordLower, false);
      this.lemmaCache.set(wordLower, wordLower);
      // console.log(`❌ 词形还原失败: "${wordLower}" (尝试了: ${possibleLemmas.join(', ')})`);
      
    } catch (error) {
      console.error(`异步加载词汇失败 "${word}":`, error);
      this.cache.set(word.toLowerCase(), false);
    }
  }

  // 批量预加载文本中的词汇
  async preloadTextVocabulary(text: string): Promise<void> {
    const words = text.match(/\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/g) || [];
    const uniqueWords = Array.from(new Set(words.map(w => w.toLowerCase())));
    
    // 只预加载缓存中没有的词汇
    const wordsToLoad = uniqueWords.filter(word => !this.cache.has(word));
    
    if (wordsToLoad.length > 0) {
      await this.preloadCommonWords(wordsToLoad);
    }
  }

  // 获取缓存统计
  getCacheStats(): any {
    return {
      cacheSize: this.cache.size,
      translationCacheSize: this.translationCache.size,
      pronunciationCacheSize: this.pronunciationCache.size,
      lemmaCacheSize: this.lemmaCache.size,
      memoryUsage: `~${((this.cache.size + this.translationCache.size) * 50 / 1024).toFixed(1)}KB`,
      backend: 'SQLite'
    };
  }

  // 设置当前词库文件（用于过滤）
  setCurrentSourceFile(sourceFile: string | null): void {
    this.currentSourceFile = sourceFile;
    // 清理缓存，因为过滤条件改变了
    this.cache.clear();
    this.translationCache.clear();
    this.pronunciationCache.clear();
    this.lemmaCache.clear();
  }

  // 获取当前词库文件
  getCurrentSourceFile(): string | null {
    return this.currentSourceFile;
  }

  // 清理缓存
  clearCache(): void {
    this.cache.clear();
    this.translationCache.clear();
    this.pronunciationCache.clear();
    this.lemmaCache.clear();
    console.log('🗑️  词汇兼容层缓存已清理');
  }
}

// 创建全局实例
export const vocabularyCompatibility = new VocabularyCompatibilityLayer();

// 创建兼容的vocabularyLevelChecker对象
export const vocabularyLevelChecker = {
  // 异步初始化方法
  async initialize(dictionaryType: string = 'TOEFL', dictionaryFile?: string): Promise<void> {
    await vocabularyCompatibility.initialize();
    
    // 设置当前词库文件，用于过滤词汇
    if (dictionaryFile) {
      vocabularyCompatibility.setCurrentSourceFile(dictionaryFile);
      
      // 预加载一些常用词汇到缓存，确保第一次渲染时能立即检查
      const commonWords = ['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'];
      try {
        await vocabularyCompatibility.preloadCommonWords(commonWords);
      } catch (error) {
        console.warn('预加载常用词汇失败:', error);
      }
    }
    
    console.log(`✅ 兼容层初始化完成 - 类型: ${dictionaryType}${dictionaryFile ? `, 文件: ${dictionaryFile}` : ''}`);
    console.log(`🎯 词汇过滤模式: ${dictionaryFile ? `仅显示 ${dictionaryFile} 中的词汇` : '显示所有词汇'}`);
  },

  // 同步方法（使用缓存）
  hasTranslation: (word: string): boolean => {
    return vocabularyCompatibility.hasTranslation(word);
  },

  getTranslation: (word: string): string[] => {
    return vocabularyCompatibility.getTranslation(word);
  },

  getUSPronunciation: (word: string): string | null => {
    return vocabularyCompatibility.getUSPronunciation(word);
  },

  getLemma: (word: string): string | null => {
    return vocabularyCompatibility.getLemma(word);
  },

  // 批量方法
  batchHasTranslation: (words: string[]): Map<string, boolean> => {
    const results = new Map<string, boolean>();
    words.forEach(word => {
      results.set(word, vocabularyCompatibility.hasTranslation(word));
    });
    return results;
  },

  batchGetTranslations: (words: string[]): Map<string, { translation?: string; usphone?: string; lemma?: string }> => {
    const results = new Map<string, { translation?: string; usphone?: string; lemma?: string }>();
    words.forEach(word => {
      const translations = vocabularyCompatibility.getTranslation(word);
      const usphone = vocabularyCompatibility.getUSPronunciation(word);
      const lemma = vocabularyCompatibility.getLemma(word);
      
      if (translations.length > 0 || usphone || lemma) {
        results.set(word, {
          ...(translations.length > 0 ? { translation: translations[0] } : {}),
          ...(usphone ? { usphone } : {}),
          ...(lemma ? { lemma } : {})
        });
      }
    });
    return results;
  },

  // 异步方法
  async preloadTextVocabulary(text: string): Promise<void> {
    return vocabularyCompatibility.preloadTextVocabulary(text);
  },

  async preloadCommonWords(words: string[]): Promise<void> {
    return vocabularyCompatibility.preloadCommonWords(words);
  },

  // 统计方法
  getMemoryStats: () => {
    return {
      type: 'SQLite-based with compatibility layer',
      ...vocabularyCompatibility.getCacheStats()
    };
  },

  // 清理方法
  clear: () => {
    vocabularyCompatibility.clearCache();
  },

  cleanup: () => {
    vocabularyCompatibility.clearCache();
  },

  // 新增：异步检查词汇方法
  hasTranslationAsync: async (word: string): Promise<boolean> => {
    return vocabularyCompatibility.hasTranslationAsync(word);
  },

  // 兼容性方法 - 基本实现
  getDifficultyLevel: (word: string) => {
    // 简单实现：如果有翻译就返回 INTERMEDIATE，否则返回 UNKNOWN
    const hasTranslation = vocabularyCompatibility.hasTranslation(word);
    return hasTranslation ? 1 : -1; // INTERMEDIATE = 1, UNKNOWN = -1
  },

  getAllCurrentDictionaryWords: () => {
    // 简单实现：返回当前缓存中的所有词汇
    return Array.from(vocabularyCompatibility['cache'].keys());
  },

  // 方法和属性
  isInitialized(): boolean {
    return vocabularyCompatibility['initialized'];
  },
  
  get initialized(): boolean {
    return vocabularyCompatibility['initialized'];
  }
};
